import React, {useState} from 'react';
import { toast } from 'react-toastify';
import { IStorefrontSettings } from '../../models/Storefront';
import { useGetGlobalStorefrontSettingsQuery, useGetStorefrontAiGenerationsQuery, useGetStorefrontsQuery, useLazyGetStorefrontLoginLinkQuery } from '../../store/endpoints/StorefrontSettingsEndpoints';
import { CommandBar, IColumn, ICommandBarItemProps, IconButton, SelectionMode, ShimmeredDetailsList, Spinner, SpinnerSize, TooltipHost } from '@fluentui/react';
import {
    ColumnGenerator,
    IconButtonColumnGenerator
} from '../../logic/Columns/ColumnGenerator';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducer';
import {useGetOrganizationEntitlementsQuery} from "../../store/endpoints/OrganizationsEndpoints";
import {useGetStorefrontTemplatesQuery} from '../../store/endpoints/StorefrontTemplateEndpoints';
import useCan, {STOREFRONT_TEMPLATES} from '../../permissions/ability';
import {IStorefrontTemplate} from '../../models/Storefront';
import Badge from '../Common/Badge';
import { SESSION_ERROR, sessionErrorFrom } from './storefrontSessionError';
import {
    activeAiChip,
    activeAiSessionId,
    aiGenerationsByStorefront,
    aiMarkerTooltip,
    showsFinishedAiMarker,
} from './storefrontAiGeneration';

/** How often the list re-checks a running generation. Only polls while one is actually running. */
const AI_POLL_INTERVAL_MS = 15000;

interface IStorefrontsTableProps {
    onDetailsClick: (storefront: IStorefrontSettings) => void;
    onNewClick: () => void;
    onCopyClick: (storefront: IStorefrontSettings) => void;
    onDeleteClick: (storefront: IStorefrontSettings) => void;
    onSaveTemplateClick?: (storefront: IStorefrontSettings) => void;
    /**
     * Reopen a running generation on its progress view. Given, the row's edit action for a storefront
     * with a live session resumes the generation instead of opening the storefront editor.
     */
    onResumeGeneration?: (sessionId: string) => void;
}

const StorefrontsTable: React.FC<IStorefrontsTableProps> = (props) => {
    const globalStorefrontSettings = useGetGlobalStorefrontSettingsQuery();
    const [getStorefrontLink] = useLazyGetStorefrontLoginLinkQuery()
    const [loadingStorefrontLink, setLoadingStorefrontLink] = useState<string | undefined>();
    const [hasRunningGeneration, setHasRunningGeneration] = useState(false);
    const org = useSelector((state: RootState) => state.settings.selectedOrganization);
    const getOrgEntitlements = useGetOrganizationEntitlementsQuery(org ?? '', {skip: !org || org === ''})
    const storefronts = useGetStorefrontsQuery();
    const canTemplates = useCan(STOREFRONT_TEMPLATES);
    const templates = useGetStorefrontTemplatesQuery(undefined, {skip: !canTemplates});

    // Storefront id -> the template captured from it. Only own-org templates carry
    // sourceStorefrontId; a global template wins over a private one, otherwise the newest.
    const templatesByStorefront = React.useMemo(() => {
        const map = new Map<string, IStorefrontTemplate>();
        for (const template of templates.data?.items ?? []) {
            const source = template.sourceStorefrontId;
            if (!source) continue;
            const existing = map.get(source);
            if (!existing) {
                map.set(source, template);
                continue;
            }
            if (!!template.isSystem && !existing.isSystem) {
                map.set(source, template);
                continue;
            }
            if (!!template.isSystem === !!existing.isSystem
                && (template.lastUpdated ?? '') > (existing.lastUpdated ?? '')) {
                map.set(source, template);
            }
        }
        return map;
    }, [templates.data]);

    // Polled only while something is actually running, so a list of finished stores costs one request.
    const aiGenerations = useGetStorefrontAiGenerationsQuery(undefined, {
        pollingInterval: hasRunningGeneration ? AI_POLL_INTERVAL_MS : 0,
    });
    const generationsByStorefront = React.useMemo(
        () => aiGenerationsByStorefront(aiGenerations.data?.items),
        [aiGenerations.data]);
    const generationFor = (item: IStorefrontSettings) =>
        item.id ? generationsByStorefront.get(item.id) : undefined;

    React.useEffect(() => {
        setHasRunningGeneration(
            (aiGenerations.data?.items ?? []).some(g => !!activeAiSessionId(g)));
    }, [aiGenerations.data]);

    const aiBadge = (item: IStorefrontSettings) => {
        const generation = generationFor(item);
        const chip = activeAiChip(generation);
        if (chip) return <Badge color={chip.color} text={chip.text} />;
        if (!showsFinishedAiMarker(generation)) return null;
        return (
            <TooltipHost content={aiMarkerTooltip(generation)}>
                <span style={{ color: 'gray', fontSize: 11, fontWeight: 600, letterSpacing: 0.5 }}>AI</span>
            </TooltipHost>
        );
    };

    const templateBadge = (item: IStorefrontSettings) => {
        const template = item.id ? templatesByStorefront.get(item.id) : undefined;
        if (!template) return null;
        return template.isSystem
            ? <Badge color='success' text='Global Template' />
            : <Badge text='Template' />;
    };
    const remaining = getOrgEntitlements.data?.storefrontLimit == undefined || storefronts.data?.total == undefined
        ? null
        : getOrgEntitlements.data?.storefrontLimit - storefronts.data?.total;

    const storefrontLink = (item: IStorefrontSettings) => {
        if (!getOrgEntitlements.data?.storefrontDomains) return <>Unknown</>
        const domains = getOrgEntitlements.data.storefrontDomains;
        if (item.domain && domains.includes(item.domain)) {
            return <a href={`https://${item.domain}/${org}/${item.liveSubUrl ?? item.subUrl}/login`} target='_blank' rel='noopener noreferrer'>Login Page</a>
        }
        if ((item.domain == null || item.domain === '') && domains.length > 0) {
            return <a href={`https://${domains[0]}/${org}/${item.liveSubUrl ?? item.subUrl}/login`} target='_blank' rel='noopener noreferrer'>Login Page</a>
        }
        return <></>
    }

    // Tracked as the sub URL actually requested rather than the storefront's, so the row's Live and
    // Preview buttons report separately instead of both reacting to whichever was clicked.
    const previewSubUrlFor = (item: IStorefrontSettings, usePreview: boolean) => {
        const liveSubUrl = item.liveSubUrl ?? item.subUrl;
        if (!liveSubUrl) return undefined;
        return usePreview ? `${liveSubUrl}-preview` : liveSubUrl;
    };

    const previewStore = async (item: IStorefrontSettings, usePreview: boolean) => {
        const subUrl = previewSubUrlFor(item, usePreview);
        if(subUrl) {
            setLoadingStorefrontLink(subUrl)
            getStorefrontLink(subUrl)
                .unwrap()
                .then( (resp) => {
                    if(resp.redirectUrl){
                        window.open(resp.redirectUrl, '_blank');
                    } else {
                        toast.error(SESSION_ERROR);
                    }
                })
                .catch((error) => {
                    toast.error(sessionErrorFrom(error));
                })
                .finally(() => setLoadingStorefrontLink(undefined) )
        }
    }

    /**
     * The row's edit action. A storefront with a live generation session sends the user back into that
     * generation instead of into the storefront editor: the assistant is rewriting the same draft, so
     * editing it by hand is the one thing that cannot work, and the progress view is what the user was
     * looking for anyway. Every other row behaves exactly as it did before.
     */
    const editClick = (item: IStorefrontSettings) => {
        const sessionId = activeAiSessionId(generationFor(item));
        if (sessionId && props.onResumeGeneration) {
            props.onResumeGeneration(sessionId);
            return;
        }
        props.onDetailsClick(item);
    };

    const columns: IColumn[] = [
        ColumnGenerator('Name', 'name', 100, undefined, (item: IStorefrontSettings) => <>{item.name} {!item.enabled && <Badge color='error' text='Disabled' />} {templateBadge(item)} {aiBadge(item)}</>),
        ColumnGenerator('Sub URL', 'subUrl', 125, 200, (item: IStorefrontSettings) => <>{item.liveSubUrl ?? item.subUrl}</>),
        ColumnGenerator('Link', 'link', 100, 200, (item: IStorefrontSettings) => storefrontLink(item)),
        ...(
            globalStorefrontSettings.data?.usePreview
            ? [
                ColumnGenerator('Preview (Live)', 'previewLive', 100, 200,
                    (item: IStorefrontSettings) => loadingStorefrontLink === previewSubUrlFor(item, false)
                        ? <Spinner size={SpinnerSize.small} />
                        : <IconButton disabled={!!loadingStorefrontLink} iconProps={{iconName: 'View'}} onClick={() => previewStore(item, false)} />,
                    false,
                    'center'),
                ColumnGenerator('Preview', 'preview', 100, 100,
                    (item: IStorefrontSettings) => item.hasPreview
                        ? loadingStorefrontLink === previewSubUrlFor(item, true)
                            ? <Spinner size={SpinnerSize.small} />
                            : <IconButton disabled={!!loadingStorefrontLink} iconProps={{iconName: 'View'}} onClick={() => previewStore(item, true)} />
                        : <span style={{ color: 'gray', fontStyle: 'italic' }}>No Draft</span>,
                    false,
                    'center'
                ),
            ]
            : [
                ColumnGenerator('Preview', 'previewLive', 100, 200,
                    (item: IStorefrontSettings) => <IconButton iconProps={{iconName: 'View'}} onClick={() => previewStore(item, false)} />,
                    false,
                    'center'),
            ]
        ),
        IconButtonColumnGenerator(editClick, 'Edit','Edit'),
        IconButtonColumnGenerator(props.onCopyClick, 'Copy','Copy'),
        ...(props.onSaveTemplateClick ? [IconButtonColumnGenerator(props.onSaveTemplateClick, 'FileTemplate', 'Save as Template')] : []),
        IconButtonColumnGenerator(props.onDeleteClick, 'Delete','Delete'),
    ]

    const commands: ICommandBarItemProps[] = [
        {
            key: 'newItem',
            text: remaining == null ? 'Loading' : `New Storefront (${remaining} remaining)`,
            iconProps: { iconName: 'Add' },
            onClick: props.onNewClick,
            disabled: storefronts.isLoading || (getOrgEntitlements.data?.storefrontLimit != null && storefronts.data?.total != null && storefronts.data.total >= getOrgEntitlements.data.storefrontLimit),
        }
    ]

    const farCommands: ICommandBarItemProps[] = [
        {
            key: 'refresh',
            text: 'Refresh',
            iconProps: { iconName: 'Refresh' },
            iconOnly: true,
            onClick: () => {storefronts.refetch(); aiGenerations.refetch()},
        }
    ]

    return (
        <div>
            <CommandBar items={commands} farItems={farCommands} />
            <ShimmeredDetailsList
                enableShimmer={storefronts.isLoading}
                items={storefronts.data?.items ?? []}
                columns={columns}
                selectionMode={SelectionMode.none}
                />
        </div>
    );
}


export default StorefrontsTable;